import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { apiFetch } from '../../../shared/lib/apiClient';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DATE_FILTER_PRESETS = [
  { value: 'this_month', label: 'This month' },
  { value: 'next_30', label: 'Next 30 days' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'all', label: 'All dates' },
];
const MAX_NOTIFICATION_EMAILS = 7;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_META = {
  scheduled: {
    label: 'Scheduled',
    badge: 'bg-sky-50 text-sky-700 border-sky-100',
    dot: 'bg-sky-500',
    surface: 'bg-sky-50/80 border-sky-100',
  },
  overdue: {
    label: 'Overdue',
    badge: 'bg-red-50 text-red-700 border-red-100',
    dot: 'bg-red-500',
    surface: 'bg-red-50/80 border-red-100',
  },
  partially_paid: {
    label: 'Partially paid',
    badge: 'bg-amber-50 text-amber-700 border-amber-100',
    dot: 'bg-amber-500',
    surface: 'bg-amber-50/80 border-amber-100',
  },
  fully_paid: {
    label: 'Fully paid',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    dot: 'bg-emerald-500',
    surface: 'bg-emerald-50/80 border-emerald-100',
  },
};

const FLAG_OPTIONS = [
  { value: 'none', label: 'No flag' },
  { value: 'follow_up', label: 'Follow up' },
  { value: 'cash_risk', label: 'Cash risk' },
  { value: 'vendor_called', label: 'Called vendor' },
  { value: 'dispute', label: 'Dispute' },
];

const CLUSTER_SINGLE_PAYMENT_THRESHOLD = 12000;
const CLUSTER_DAY_TOTAL_THRESHOLD = 32000;
const CLUSTER_WEEK_TOTAL_THRESHOLD = 90000;

const sumBy = (items, pick) => items.reduce((acc, item) => acc + pick(item), 0);

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const parseDateKey = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
};

const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1);

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const startOfWeek = (date) => {
  const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = base.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  return addDays(base, offset);
};

const endOfWeek = (date) => addDays(startOfWeek(date), 6);

const formatDate = (dateKey, options) => {
  const parsed = parseDateKey(dateKey);
  if (!parsed) return 'N/A';
  return parsed.toLocaleDateString('en-US', options);
};

const formatDateLong = (dateKey) =>
  formatDate(dateKey, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const formatDateShort = (dateKey) =>
  formatDate(dateKey, {
    month: 'short',
    day: 'numeric',
  });

const formatMonthTitle = (dateKey) =>
  formatDate(dateKey, {
    month: 'long',
    year: 'numeric',
  });

const percentDelta = (current, previous) => {
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / previous) * 100;
};

const timeAgo = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Not synced';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const candidate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(candidate.getTime())) return null;
  return candidate;
};

const extractLastSyncedAt = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const candidates = [
    payload.lastSyncedAt,
    payload.last_synced_at,
    payload.syncedAt,
    payload.synced_at,
    payload.meta?.lastSyncedAt,
    payload.meta?.last_synced_at,
    payload.sync?.lastSyncedAt,
    payload.sync?.last_synced_at,
  ];

  for (const candidate of candidates) {
    const parsed = parseDateValue(candidate);
    if (parsed) return parsed;
  }

  return null;
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.scheduled;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border ${meta.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const PaymentCalendar = ({ loaded, onNavigateToIntegrations }) => {
  const { companies, activeCompany, switchCompany } = useAuth();

  const companyOptions = useMemo(() => {
    return companies.map((companyEntry) => ({
      id: companyEntry.companyId,
      name: companyEntry.company?.name || 'Unnamed company',
    }));
  }, [companies]);

  const [paymentsByCompany, setPaymentsByCompany] = useState({});
  const [localCompanyId, setLocalCompanyId] = useState(companyOptions[0]?.id || null);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [focusedDateKey, setFocusedDateKey] = useState(toDateKey(new Date()));
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partyFilter, setPartyFilter] = useState('all');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState('all');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [detailNoteDraft, setDetailNoteDraft] = useState('');
  const [detailFlagDraft, setDetailFlagDraft] = useState('none');
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [detailSaveMessage, setDetailSaveMessage] = useState('');

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [notificationEmailDraft, setNotificationEmailDraft] = useState('');
  const [notificationError, setNotificationError] = useState('');
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);

  useEffect(() => {
    if (!activeCompany?.companyId && !localCompanyId && companyOptions[0]?.id) {
      setLocalCompanyId(companyOptions[0].id);
    }
  }, [companyOptions, activeCompany, localCompanyId]);

  const selectedCompanyId = activeCompany?.companyId || localCompanyId || companyOptions[0]?.id || '';

  const selectedCompanyName = useMemo(() => {
    const match = companyOptions.find((company) => company.id === selectedCompanyId);
    return match?.name || 'Company';
  }, [companyOptions, selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) return;

    let cancelled = false;

    const loadPayments = async () => {
      setIsLoadingPayments(true);
      setLoadError('');

      try {
        const params = new URLSearchParams({
          source: 'auto',
          limit: '1000',
          includeConflicts: 'true',
        });

        const res = await apiFetch(`/api/calendar/payments?${params.toString()}`, {
          companyId: selectedCompanyId,
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || 'Failed to load calendar payments');
        }

        if (cancelled) return;

        const nextPayments = data.payments || [];
        setPaymentsByCompany((previous) => ({
          ...previous,
          [selectedCompanyId]: nextPayments,
        }));
        setConflicts(data.conflicts || []);
        setLastSyncedAt(extractLastSyncedAt(data));

        setSelectedPaymentId((previous) =>
          nextPayments.some((payment) => payment.id === previous) ? previous : null
        );
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || 'Failed to load calendar data');
          setPaymentsByCompany((previous) => ({
            ...previous,
            [selectedCompanyId]: previous[selectedCompanyId] || [],
          }));
        }
      } finally {
        if (!cancelled) setIsLoadingPayments(false);
      }
    };

    loadPayments();

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) return;

    let cancelled = false;

    const loadNotificationSettings = async () => {
      try {
        const res = await apiFetch('/api/calendar/settings', {
          companyId: selectedCompanyId,
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || 'Failed to load calendar settings');
        }

        if (cancelled) return;

        const settings = data.settings || {};
        setNotificationsEnabled(!!settings.notificationsEnabled);
        setNotificationEmails(Array.isArray(settings.notificationEmails) ? settings.notificationEmails : []);
        setNotificationError('');
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || 'Failed to load calendar settings');
        }
      }
    };

    loadNotificationSettings();

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  const companyPayments = useMemo(() => {
    return paymentsByCompany[selectedCompanyId] || [];
  }, [paymentsByCompany, selectedCompanyId]);

  const uniquePartyOptions = useMemo(() => {
    return [...new Set(companyPayments.map((payment) => payment.partyName))].sort((a, b) => a.localeCompare(b));
  }, [companyPayments]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const parsedMinAmount = minAmount === '' ? null : Number(minAmount);
  const parsedMaxAmount = maxAmount === '' ? null : Number(maxAmount);

  const filteredPayments = useMemo(() => {
    return companyPayments.filter((payment) => {
      if (statusFilter !== 'all' && payment.status !== statusFilter) return false;
      if (partyFilter !== 'all' && payment.partyName !== partyFilter) return false;
      if (parsedMinAmount != null && !Number.isNaN(parsedMinAmount) && payment.amount < parsedMinAmount) return false;
      if (parsedMaxAmount != null && !Number.isNaN(parsedMaxAmount) && payment.amount > parsedMaxAmount) return false;
      if (rangeStart && payment.dueDate < rangeStart) return false;
      if (rangeEnd && payment.dueDate > rangeEnd) return false;

      if (normalizedSearch) {
        const searchTargets = [
          payment.partyName,
          payment.invoiceNumber,
          formatCurrency(payment.amount),
          `${payment.amount}`,
        ]
          .join(' ')
          .toLowerCase();

        if (!searchTargets.includes(normalizedSearch)) return false;
      }

      return true;
    });
  }, [
    companyPayments,
    normalizedSearch,
    parsedMaxAmount,
    parsedMinAmount,
    partyFilter,
    rangeEnd,
    rangeStart,
    statusFilter,
  ]);

  const filteredByDate = useMemo(() => {
    const grouped = {};
    filteredPayments.forEach((payment) => {
      if (!grouped[payment.dueDate]) grouped[payment.dueDate] = [];
      grouped[payment.dueDate].push(payment);
    });

    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort((a, b) => b.amount - a.amount);
    });

    return grouped;
  }, [filteredPayments]);

  const selectedPayment = useMemo(
    () => companyPayments.find((payment) => payment.id === selectedPaymentId) || null,
    [companyPayments, selectedPaymentId]
  );

  const selectedSavedNote = selectedPayment
    ? (selectedPayment.quickNote ?? selectedPayment.notes ?? '')
    : '';
  const selectedSavedFlag = selectedPayment
    ? (selectedPayment.flag ?? 'none')
    : 'none';

  const hasUnsavedDetailChanges = Boolean(
    selectedPayment &&
      (detailNoteDraft !== selectedSavedNote || detailFlagDraft !== selectedSavedFlag)
  );

  const focusedDate = parseDateKey(focusedDateKey) || new Date();
  const focusedMonthStart = startOfMonth(focusedDate);
  const focusedMonthEnd = endOfMonth(focusedDate);

  const periodTotals = useMemo(() => {
    const startKey = toDateKey(focusedMonthStart);
    const endKey = toDateKey(focusedMonthEnd);
    const monthItems = filteredPayments.filter((payment) => payment.dueDate >= startKey && payment.dueDate <= endKey);
    return {
      label: 'Visible month',
      count: monthItems.length,
      amount: sumBy(monthItems, (item) => item.amount),
    };
  }, [filteredPayments, focusedMonthEnd, focusedMonthStart]);

  const dateFilterLabel = useMemo(() => {
    if (rangeStart && rangeEnd) return `${formatDateShort(rangeStart)} - ${formatDateShort(rangeEnd)}`;
    if (rangeStart) return `From ${formatDateShort(rangeStart)}`;
    if (rangeEnd) return `Until ${formatDateShort(rangeEnd)}`;
    return 'All payment dates';
  }, [rangeEnd, rangeStart]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        searchTerm ||
          statusFilter !== 'all' ||
          partyFilter !== 'all' ||
          minAmount ||
          maxAmount ||
          rangeStart ||
          rangeEnd
      ),
    [maxAmount, minAmount, partyFilter, rangeEnd, rangeStart, searchTerm, statusFilter]
  );

  const overdueStats = useMemo(() => {
    const overdue = filteredPayments.filter((payment) => payment.status === 'overdue');
    return {
      count: overdue.length,
      amount: sumBy(overdue, (payment) => payment.outstandingAmount),
    };
  }, [filteredPayments]);

  const trendStats = useMemo(() => {
    const now = new Date();

    const currentWeekStart = startOfWeek(now);
    const currentWeekEnd = endOfWeek(now);
    const previousWeekStart = addDays(currentWeekStart, -7);
    const previousWeekEnd = addDays(currentWeekEnd, -7);

    const currentMonthStart = startOfMonth(now);
    const currentMonthEnd = endOfMonth(now);
    const previousMonthStart = startOfMonth(addMonths(now, -1));
    const previousMonthEnd = endOfMonth(addMonths(now, -1));

    const windowSummary = (start, end) => {
      const startKey = toDateKey(start);
      const endKey = toDateKey(end);
      const inWindow = filteredPayments.filter((payment) => payment.dueDate >= startKey && payment.dueDate <= endKey);
      return {
        count: inWindow.length,
        amount: sumBy(inWindow, (payment) => payment.amount),
      };
    };

    const thisWeek = windowSummary(currentWeekStart, currentWeekEnd);
    const lastWeek = windowSummary(previousWeekStart, previousWeekEnd);
    const thisMonth = windowSummary(currentMonthStart, currentMonthEnd);
    const lastMonth = windowSummary(previousMonthStart, previousMonthEnd);

    return {
      thisWeek,
      lastWeek,
      thisMonth,
      lastMonth,
      weekDelta: percentDelta(thisWeek.amount, lastWeek.amount),
      monthDelta: percentDelta(thisMonth.amount, lastMonth.amount),
    };
  }, [filteredPayments]);

  const clusterAlerts = useMemo(() => {
    const daily = Object.entries(filteredByDate)
      .map(([dateKey, records]) => {
        const total = sumBy(records, (payment) => payment.amount);
        const largeCount = records.filter((payment) => payment.amount >= CLUSTER_SINGLE_PAYMENT_THRESHOLD).length;
        return {
          dateKey,
          total,
          count: records.length,
          largeCount,
        };
      })
      .filter((entry) => entry.total >= CLUSTER_DAY_TOTAL_THRESHOLD || entry.largeCount >= 2)
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);

    const weeklyMap = {};

    filteredPayments.forEach((payment) => {
      const weekKey = toDateKey(startOfWeek(parseDateKey(payment.dueDate) || new Date()));
      if (!weeklyMap[weekKey]) {
        weeklyMap[weekKey] = {
          weekKey,
          count: 0,
          total: 0,
          largeCount: 0,
        };
      }
      weeklyMap[weekKey].count += 1;
      weeklyMap[weekKey].total += payment.amount;
      if (payment.amount >= CLUSTER_SINGLE_PAYMENT_THRESHOLD) {
        weeklyMap[weekKey].largeCount += 1;
      }
    });

    const weekly = Object.values(weeklyMap)
      .filter((entry) => entry.total >= CLUSTER_WEEK_TOTAL_THRESHOLD || entry.largeCount >= 4)
      .sort((a, b) => b.total - a.total)
      .slice(0, 2);

    return { daily, weekly };
  }, [filteredByDate, filteredPayments]);

  const conflictItemsForSelectedPayment = useMemo(() => {
    if (!selectedPayment?.id) return [];
    return conflicts.filter((conflict) => conflict.paymentId === selectedPayment.id);
  }, [conflicts, selectedPayment]);

  useEffect(() => {
    if (!selectedPayment) {
      setDetailNoteDraft('');
      setDetailFlagDraft('none');
      setDetailSaveMessage('');
      return;
    }

    setDetailNoteDraft(selectedSavedNote);
    setDetailFlagDraft(selectedSavedFlag);
    setDetailSaveMessage('');
  }, [selectedPayment, selectedSavedNote, selectedSavedFlag]);

  const handleCompanyChange = (nextCompanyId) => {
    if (!nextCompanyId) return;
    if (activeCompany?.companyId) {
      switchCompany(nextCompanyId);
    } else {
      setLocalCompanyId(nextCompanyId);
      localStorage.setItem('active_company_id', nextCompanyId);
    }
    setLoadError('');
    setDetailSaveMessage('');
    setSelectedPaymentId(null);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPartyFilter('all');
    setMinAmount('');
    setMaxAmount('');
    setRangeStart('');
    setRangeEnd('');
    setActiveDatePreset('all');
  };

  const applyDatePreset = (presetValue) => {
    const now = new Date();
    let nextStart = '';
    let nextEnd = '';

    if (presetValue === 'this_month') {
      nextStart = toDateKey(startOfMonth(now));
      nextEnd = toDateKey(endOfMonth(now));
    } else if (presetValue === 'next_30') {
      nextStart = toDateKey(now);
      nextEnd = toDateKey(addDays(now, 30));
    } else if (presetValue === 'this_quarter') {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const quarterStart = new Date(now.getFullYear(), quarterStartMonth, 1);
      const quarterEnd = endOfMonth(new Date(now.getFullYear(), quarterStartMonth + 2, 1));
      nextStart = toDateKey(quarterStart);
      nextEnd = toDateKey(quarterEnd);
    }

    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
    setActiveDatePreset(presetValue);
    setFocusedDateKey(nextStart || toDateKey(now));
  };

  const handleRangeStartChange = (nextStart) => {
    setRangeStart(nextStart);
    setActiveDatePreset(!nextStart && !rangeEnd ? 'all' : 'custom');
    if (nextStart && rangeEnd && nextStart > rangeEnd) {
      setRangeEnd(nextStart);
    }
    if (nextStart) {
      setFocusedDateKey(nextStart);
    }
  };

  const handleRangeEndChange = (nextEnd) => {
    setRangeEnd(nextEnd);
    setActiveDatePreset(!nextEnd && !rangeStart ? 'all' : 'custom');
    if (nextEnd && rangeStart && nextEnd < rangeStart) {
      setRangeStart(nextEnd);
    }
    if (nextEnd) {
      setFocusedDateKey(nextEnd);
    }
  };

  const shiftFocusedMonth = (monthsOffset) => {
    setFocusedDateKey(toDateKey(addMonths(startOfMonth(focusedDate), monthsOffset)));
  };

  const jumpToToday = () => {
    setFocusedDateKey(toDateKey(new Date()));
  };

  const handleAddNotificationEmail = () => {
    const nextEmail = notificationEmailDraft.trim().toLowerCase();
    if (!nextEmail) return;

    if (notificationEmails.length >= MAX_NOTIFICATION_EMAILS) {
      setNotificationError(`You can add up to ${MAX_NOTIFICATION_EMAILS} email addresses.`);
      return;
    }

    if (!EMAIL_PATTERN.test(nextEmail)) {
      setNotificationError('Enter a valid email address.');
      return;
    }

    if (notificationEmails.includes(nextEmail)) {
      setNotificationError('Email already exists.');
      return;
    }

    setNotificationEmails((previous) => [...previous, nextEmail]);
    setNotificationEmailDraft('');
    setNotificationError('');
  };

  const handleRemoveNotificationEmail = (emailToRemove) => {
    setNotificationEmails((previous) => previous.filter((email) => email !== emailToRemove));
    setNotificationError('');
  };

  const persistNotificationSettings = async (settingsPayload) => {
    if (!selectedCompanyId) return false;

    setIsSavingNotifications(true);
    setNotificationError('');

    try {
      const res = await apiFetch('/api/calendar/settings', {
        method: 'PUT',
        body: JSON.stringify(settingsPayload),
        companyId: selectedCompanyId,
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || 'Failed to save calendar settings');
      }

      const settings = data.settings || {};
      setNotificationsEnabled(!!settings.notificationsEnabled);
      setNotificationEmails(Array.isArray(settings.notificationEmails) ? settings.notificationEmails : []);
      return true;
    } catch (error) {
      setNotificationError(error.message || 'Failed to save calendar settings');
      return false;
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleNotificationToggle = async () => {
    if (isSavingNotifications) return;

    if (notificationsEnabled) {
      await persistNotificationSettings({ notificationsEnabled: false });
      return;
    }

    setNotificationError('');
    setIsNotificationModalOpen(true);
  };

  const handleCloseNotificationModal = () => {
    setNotificationError('');
    setIsNotificationModalOpen(false);
  };

  const handleSaveNotifications = async () => {
    if (notificationEmails.length === 0) {
      setNotificationError('Add at least one email address.');
      return;
    }

    if (notificationEmails.length > MAX_NOTIFICATION_EMAILS) {
      setNotificationError(`You can add up to ${MAX_NOTIFICATION_EMAILS} email addresses.`);
      return;
    }

    const saved = await persistNotificationSettings({
      notificationsEnabled: true,
      notificationEmails,
    });

    if (saved) {
      setNotificationError('');
      setIsNotificationModalOpen(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedCompanyId) return;

    setIsRefreshing(true);
    setLoadError('');
    try {
      const res = await apiFetch('/api/calendar/refresh', {
        method: 'POST',
        body: JSON.stringify({ source: 'auto' }),
        companyId: selectedCompanyId,
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || 'Refresh failed');
      }

      const nextPayments = data.payments || [];
      setPaymentsByCompany((previous) => ({
        ...previous,
        [selectedCompanyId]: nextPayments,
      }));
      setConflicts(data.conflicts || []);
      setLastSyncedAt(extractLastSyncedAt(data) || new Date());
      setSelectedPaymentId((previous) =>
        nextPayments.some((payment) => payment.id === previous) ? previous : null
      );
    } catch (error) {
      setLoadError(error.message || 'Refresh failed');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!selectedPayment || !hasUnsavedDetailChanges || isSavingDetails) return;

    setIsSavingDetails(true);
    setDetailSaveMessage('');
    setLoadError('');

    try {
      const res = await apiFetch(`/api/calendar/payments/${encodeURIComponent(selectedPayment.id)}/details`, {
        method: 'PUT',
        body: JSON.stringify({
          note: detailNoteDraft,
          flag: detailFlagDraft,
        }),
        companyId: selectedCompanyId,
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || 'Failed to save payment details');
      }

      setPaymentsByCompany((previous) => ({
        ...previous,
        [selectedCompanyId]: (previous[selectedCompanyId] || []).map((payment) =>
          payment.id === selectedPayment.id
            ? {
                ...payment,
                notes: detailNoteDraft,
                quickNote: detailNoteDraft,
                flag: detailFlagDraft,
              }
            : payment
        ),
      }));

      if (Array.isArray(data.conflicts)) {
        setConflicts(data.conflicts);
      }

      setLastSyncedAt(parseDateValue(data.updatedAt || data.updated_at) || new Date());

      setDetailSaveMessage('Saved.');
    } catch (error) {
      setLoadError(error.message || 'Failed to save payment details');
    } finally {
      setIsSavingDetails(false);
    }
  };

  const renderPaymentPill = (payment) => {
    const meta = STATUS_META[payment.status] || STATUS_META.scheduled;

    return (
      <button
        key={payment.id}
        onClick={() => setSelectedPaymentId(payment.id)}
        className={`w-full text-left px-2 py-1.5 rounded-md border ${meta.surface} hover:bg-white transition-colors`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-zinc-800 truncate">
            {payment.partyName}
          </span>
          <span className="text-[11px] tabular-nums text-zinc-700">{formatCurrency(payment.amount)}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          <span className="text-[10px] text-zinc-500">{payment.invoiceNumber}</span>
          {payment.flag !== 'none' && (
            <span className="text-[10px] text-zinc-500 ml-auto uppercase tracking-[0.08em]">Flagged</span>
          )}
        </div>
      </button>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(focusedDate);
    const monthEnd = endOfMonth(focusedDate);
    const gridStart = startOfWeek(monthStart);
    const gridEnd = endOfWeek(monthEnd);

    const cells = [];
    for (let current = gridStart; current <= gridEnd; current = addDays(current, 1)) {
      cells.push(new Date(current.getFullYear(), current.getMonth(), current.getDate()));
    }

    return (
      <div className="border-t border-zinc-100">
        <div className="grid grid-cols-7 border-b border-zinc-100 bg-zinc-50/50">
          {DAY_NAMES.map((day) => (
            <div key={day} className="py-2.5 text-center text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((cellDate) => {
            const dateKey = toDateKey(cellDate);
            const dayPayments = filteredByDate[dateKey] || [];
            const dayTotal = sumBy(dayPayments, (payment) => payment.amount);
            const outsideMonth = cellDate.getMonth() !== focusedDate.getMonth();
            const isFocusedDate = dateKey === focusedDateKey;
            const hasOverdue = dayPayments.some((payment) => payment.status === 'overdue');
            const isClustered =
              dayTotal >= CLUSTER_DAY_TOTAL_THRESHOLD ||
              dayPayments.filter((payment) => payment.amount >= CLUSTER_SINGLE_PAYMENT_THRESHOLD).length >= 2;

            return (
              <div
                key={dateKey}
                className={`min-h-[130px] border-r border-b border-zinc-100 p-2.5 ${outsideMonth ? 'bg-zinc-50/50' : 'bg-white'} ${
                  isFocusedDate ? 'ring-1 ring-zinc-800/20 ring-inset' : ''
                } ${hasOverdue ? 'shadow-[inset_0_2px_0_0_rgba(239,68,68,0.45)]' : ''}`}
                onClick={() => setFocusedDateKey(dateKey)}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <span className={`text-xs font-medium ${outsideMonth ? 'text-zinc-400' : 'text-zinc-700'}`}>
                    {cellDate.getDate()}
                  </span>
                  {dayPayments.length > 0 && (
                    <span className="text-[10px] text-zinc-500 tabular-nums">{formatCurrency(dayTotal)}</span>
                  )}
                </div>

                <div className="space-y-1">
                  {dayPayments.slice(0, 2).map((payment) => renderPaymentPill(payment))}
                  {dayPayments.length > 2 && (
                    <button
                      className="w-full text-left px-2 py-1 rounded-md bg-zinc-100 text-[10px] text-zinc-600 hover:bg-zinc-200 transition-colors"
                      onClick={() => setFocusedDateKey(dateKey)}
                    >
                      +{dayPayments.length - 2} more payments
                    </button>
                  )}
                  {isClustered && (
                    <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                      Payment cluster
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const viewLabel = formatMonthTitle(focusedDateKey);

  return (
    <div data-tour="calendar-page" className="max-w-[1380px] mx-auto">
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '200ms' }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-light text-zinc-900 mb-1">Calendar</h1>
            <p className="text-sm text-zinc-400">QuickBooks payment tracking workspace </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2">
              <span className="text-[11px] uppercase tracking-[0.12em] text-zinc-600">
                Enable notifications
              </span>
              <button
                type="button"
                onClick={handleNotificationToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationsEnabled ? 'bg-zinc-900' : 'bg-zinc-300'
                } ${isSavingNotifications ? 'cursor-not-allowed opacity-60' : ''}`}
                role="switch"
                aria-checked={notificationsEnabled}
                aria-label="Enable notifications"
                disabled={isSavingNotifications}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    notificationsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            <button
              onClick={handleRefresh}
              className="px-3 py-2 border border-zinc-200 text-zinc-600 text-[11px] uppercase tracking-[0.12em] rounded-md hover:border-zinc-400 hover:text-zinc-800 transition-colors disabled:opacity-50"
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Last synced {timeAgo(lastSyncedAt)}
          </span>
          <button
            onClick={onNavigateToIntegrations}
            className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-2.5 py-1 hover:border-zinc-400 hover:text-zinc-700 transition-colors"
          >
            QuickBooks integrations
          </button>
          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-2.5 py-1">
            Company: {selectedCompanyName}
          </span>
          {isLoadingPayments && (
            <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-2.5 py-1">
              Loading calendar data...
            </span>
          )}
        </div>
        {loadError && (
          <p className="mt-2 text-xs text-red-600">{loadError}</p>
        )}
      </div>

      <div className="space-y-4">
        <section className="space-y-4">
          <div
            className="bg-white border border-zinc-100 rounded-lg p-4 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '280ms' }}
          >
            <div className="rounded-xl border border-zinc-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500">Date filter</p>
                  <p className="mt-1 text-sm text-zinc-800">{dateFilterLabel}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {DATE_FILTER_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => applyDatePreset(preset.value)}
                      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.1em] transition-colors ${
                        activeDatePreset === preset.value
                          ? 'bg-zinc-900 text-white shadow-sm'
                          : 'bg-white/80 border border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-800'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">From</span>
                  <input
                    type="date"
                    value={rangeStart}
                    onChange={(event) => handleRangeStartChange(event.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white border border-zinc-200 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  />
                </label>

                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">To</span>
                  <input
                    type="date"
                    value={rangeEnd}
                    onChange={(event) => handleRangeEndChange(event.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white border border-zinc-200 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  />
                </label>

                <button
                  onClick={() => applyDatePreset('all')}
                  className="mt-[18px] px-3 py-2 text-[11px] uppercase tracking-[0.12em] rounded-md border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400 hover:text-zinc-800 transition-colors"
                >
                  Clear date
                </button>

                <button
                  onClick={jumpToToday}
                  className="mt-[18px] px-3 py-2 text-[11px] uppercase tracking-[0.12em] rounded-md border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400 hover:text-zinc-800 transition-colors"
                >
                  Focus today
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Search</span>
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Vendor, invoice, amount"
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Status</span>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                >
                  <option value="all">All statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="overdue">Overdue</option>
                  <option value="partially_paid">Partially paid</option>
                  <option value="fully_paid">Fully paid</option>
                </select>
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Vendor/Customer</span>
                <select
                  value={partyFilter}
                  onChange={(event) => setPartyFilter(event.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                >
                  <option value="all">All parties</option>
                  {uniquePartyOptions.map((partyName) => (
                    <option key={partyName} value={partyName}>{partyName}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Min amount</span>
                <input
                  value={minAmount}
                  onChange={(event) => setMinAmount(event.target.value)}
                  placeholder="0"
                  type="number"
                  min="0"
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Max amount</span>
                <input
                  value={maxAmount}
                  onChange={(event) => setMaxAmount(event.target.value)}
                  placeholder="50000"
                  type="number"
                  min="0"
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Company</span>
                <select
                  value={selectedCompanyId}
                  onChange={(event) => handleCompanyChange(event.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                >
                  {companyOptions.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-zinc-500">{filteredPayments.length} payments match current filters.</p>
              <button
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                className="px-3 py-2 text-[11px] uppercase tracking-[0.12em] rounded-md border border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Clear all filters
              </button>
            </div>
          </div>

          <div
            className="grid grid-cols-2 lg:grid-cols-3 gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '320ms' }}
          >
            <div className="bg-white border border-zinc-100 rounded-lg p-4">
              <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Total outstanding</p>
              <p className="mt-1 text-xl font-light text-zinc-900 tabular-nums">{formatCurrency(sumBy(filteredPayments, (p) => p.outstandingAmount))}</p>
              <p className="text-xs text-zinc-500 mt-1">Across {filteredPayments.length} payments</p>
            </div>

            <div className="bg-white border border-zinc-100 rounded-lg p-4">
              <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Overdue outstanding</p>
              <p className="mt-1 text-xl font-light text-red-700 tabular-nums">{formatCurrency(overdueStats.amount)}</p>
              <p className="text-xs text-zinc-500 mt-1">{overdueStats.count} overdue</p>
            </div>

            <div className="bg-white border border-zinc-100 rounded-lg p-4">
              <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">This month vs last month payments</p>
              <p className="mt-1 text-xl font-light text-zinc-900 tabular-nums">{formatCurrency(trendStats.thisMonth.amount)}</p>
              <p className={`text-xs mt-1 ${trendStats.monthDelta >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {trendStats.monthDelta >= 0 ? '+' : ''}{trendStats.monthDelta.toFixed(1)}%
              </p>
            </div>
          </div>

          {(clusterAlerts.daily.length > 0 || clusterAlerts.weekly.length > 0) && (
            <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-amber-900">Payment clustering alerts</p>
                <span className="text-[11px] text-amber-700 px-2 py-1 rounded-full border border-amber-200 bg-white/70">
                  cash-flow risk watch
                </span>
              </div>
              <div className="mt-3 max-h-40 overflow-y-auto pr-1 space-y-2">
                {clusterAlerts.daily.map((alert) => (
                  <div key={`daily-${alert.dateKey}`} className="rounded-md border border-amber-200 bg-white/75 px-3 py-2">
                    <p className="text-xs font-medium text-amber-900">{formatDateLong(alert.dateKey)}</p>
                    <p className="text-[11px] text-amber-800 mt-0.5">{alert.count} payments · {formatCurrency(alert.total)}</p>
                  </div>
                ))}
                {clusterAlerts.weekly.map((alert) => (
                  <div key={`weekly-${alert.weekKey}`} className="rounded-md border border-amber-200 bg-white/75 px-3 py-2">
                    <p className="text-xs font-medium text-amber-900">Week of {formatDateShort(alert.weekKey)}</p>
                    <p className="text-[11px] text-amber-800 mt-0.5">{alert.count} payments · {formatCurrency(alert.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '360ms' }}
          >
            <div className="px-4 py-3 border-b border-zinc-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-zinc-900">{viewLabel}</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {periodTotals.label}: {periodTotals.count} payments · {formatCurrency(periodTotals.amount)}
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">Range: {dateFilterLabel}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 overflow-hidden">
                  <button
                    onClick={() => shiftFocusedMonth(-1)}
                    className="px-3 py-2 text-[11px] uppercase tracking-[0.12em] text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
                  >
                    Prev
                  </button>
                  <button
                    onClick={jumpToToday}
                    className="px-3 py-2 border-l border-r border-zinc-200 text-[11px] uppercase tracking-[0.12em] text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => shiftFocusedMonth(1)}
                    className="px-3 py-2 text-[11px] uppercase tracking-[0.12em] text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
                  >
                    Next
                  </button>
                </div>

                <input
                  type="month"
                  value={focusedDateKey.slice(0, 7)}
                  onChange={(event) => {
                    const [year, month] = event.target.value.split('-').map(Number);
                    if (year && month) {
                      setFocusedDateKey(toDateKey(new Date(year, month - 1, 1)));
                    }
                  }}
                  className="px-2 py-2 bg-zinc-50 border border-zinc-200 rounded text-sm text-zinc-700"
                />

                <input
                  type="date"
                  value={focusedDateKey}
                  onChange={(event) => setFocusedDateKey(event.target.value)}
                  className="px-2 py-2 bg-zinc-50 border border-zinc-200 rounded text-sm text-zinc-700"
                />
              </div>
            </div>

            {renderMonthView()}
          </div>

          {conflicts.length > 0 && (
            <div className="bg-white border border-zinc-100 rounded-lg p-4">
              <p className="text-sm font-medium text-zinc-900">External change log</p>
              <p className="text-xs text-zinc-500 mt-1">Detected updates from QuickBooks refresh actions.</p>
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto pr-1">
                {conflicts.slice(0, 8).map((conflict) => (
                  <div key={conflict.id} className="rounded-md border border-zinc-100 px-3 py-2">
                    <p className="text-xs font-medium text-zinc-800">
                      {conflict.invoiceNumber} · {conflict.partyName}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {new Date(conflict.changedAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    <p className="text-[11px] text-zinc-600 mt-1">{conflict.changes.join(' · ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {isNotificationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-zinc-900/45 backdrop-blur-[1px]" onClick={handleCloseNotificationModal} />
          <div className="relative w-full max-w-3xl rounded-2xl border border-zinc-200 bg-white shadow-[0_25px_80px_rgba(24,24,27,0.2)]">
            <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Enable notifications</p>
                <p className="text-sm text-zinc-700 mt-1">Configure recipients for calendar notifications.</p>
              </div>
              <button
                type="button"
                onClick={handleCloseNotificationModal}
                className="w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
                aria-label="Close notifications setup"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M12 4L4 12M4 4l8 8" />
                </svg>
              </button>
            </div>

            <div className="p-5">
              <div className="rounded-md border border-zinc-100 bg-zinc-50/70 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Notification emails</p>
                  <p className="text-[11px] text-zinc-500">
                    {notificationEmails.length}/{MAX_NOTIFICATION_EMAILS}
                  </p>
                </div>
                <p className="text-xs text-zinc-500 mt-1">Add up to {MAX_NOTIFICATION_EMAILS} recipients.</p>

                <div className="mt-2 flex flex-wrap gap-2">
                  {notificationEmails.length === 0 ? (
                    <p className="text-xs text-zinc-500">No emails added yet.</p>
                  ) : (
                    notificationEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-zinc-200 bg-white text-xs text-zinc-700"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveNotificationEmail(email)}
                          className="text-zinc-500 hover:text-zinc-800"
                          aria-label={`Remove ${email}`}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    value={notificationEmailDraft}
                    onChange={(event) => setNotificationEmailDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddNotificationEmail();
                      }
                    }}
                    placeholder="name@company.com"
                    className="flex-1 min-w-[220px] px-3 py-2 bg-white border border-zinc-200 rounded-md text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  />
                  <button
                    type="button"
                    onClick={handleAddNotificationEmail}
                    disabled={notificationEmails.length >= MAX_NOTIFICATION_EMAILS}
                    className="px-3 py-2 bg-zinc-900 text-white text-[11px] uppercase tracking-[0.12em] rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  >
                    Add email
                  </button>
                </div>
              </div>

              {notificationError && <p className="mt-3 text-xs text-red-600">{notificationError}</p>}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCloseNotificationModal}
                  className="px-3 py-2 border border-zinc-200 text-zinc-600 text-[11px] uppercase tracking-[0.12em] rounded-md hover:border-zinc-400 hover:text-zinc-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveNotifications}
                  disabled={isSavingNotifications}
                  className="px-4 py-2 bg-zinc-900 text-white text-[11px] uppercase tracking-[0.12em] rounded-md hover:bg-zinc-800 transition-colors"
                >
                  {isSavingNotifications ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPayment && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-zinc-900/45 backdrop-blur-[1px]" onClick={() => setSelectedPaymentId(null)} />
          <div className="relative w-full max-w-4xl max-h-[88vh] overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_25px_80px_rgba(24,24,27,0.2)]">
            <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-700 px-5 py-4 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-300">Payment detail</p>
                  <h2 className="mt-1 text-lg font-medium">{selectedPayment.invoiceNumber}</h2>
                  <p className="text-sm text-zinc-300 mt-1">{selectedPayment.partyName} · {selectedPayment.partyType}</p>
                </div>
                <button
                  onClick={() => setSelectedPaymentId(null)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M12 4L4 12M4 4l8 8" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(88vh-84px)] p-5">
              <div className="flex items-center gap-2">
                <StatusBadge status={selectedPayment.status} />
                <span className="text-[11px] px-2 py-1 rounded-full border border-zinc-200 text-zinc-500">
                  Source {(selectedPayment.originIntegration || selectedPayment.source || 'unknown').replace(/_/g, ' ')}
                </span>
                {selectedPayment.lastExternalUpdate && (
                  <span className="text-[11px] px-2 py-1 rounded-full border border-zinc-200 text-zinc-500">
                    Updated {new Date(selectedPayment.lastExternalUpdate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div className="p-3 rounded-md bg-zinc-50 border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Amount</p>
                  <p className="mt-1 font-medium tabular-nums text-zinc-900">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div className="p-3 rounded-md bg-zinc-50 border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Outstanding</p>
                  <p className="mt-1 font-medium tabular-nums text-zinc-900">{formatCurrency(selectedPayment.outstandingAmount)}</p>
                </div>
                <div className="p-3 rounded-md bg-zinc-50 border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Due date</p>
                  <p className="mt-1 font-medium text-zinc-900">{formatDateLong(selectedPayment.dueDate)}</p>
                </div>
                <div className="p-3 rounded-md bg-zinc-50 border border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Terms</p>
                  <p className="mt-1 font-medium text-zinc-900">{selectedPayment.terms}</p>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 mb-2">Line items</p>
                <div className="border border-zinc-100 rounded-md overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-100">
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.12em] text-zinc-400">Description</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.12em] text-zinc-400">Qty</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.12em] text-zinc-400">Rate</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.12em] text-zinc-400">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedPayment.lineItems || []).map((line, index) => (
                        <tr key={`${selectedPayment.id}-line-${index}`} className="border-b border-zinc-100 last:border-0">
                          <td className="px-3 py-2 text-sm text-zinc-700">{line.description}</td>
                          <td className="px-3 py-2 text-sm text-zinc-700 text-right tabular-nums">{line.qty}</td>
                          <td className="px-3 py-2 text-sm text-zinc-700 text-right tabular-nums">{formatCurrency(line.rate)}</td>
                          <td className="px-3 py-2 text-sm text-zinc-700 text-right tabular-nums">{formatCurrency(line.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Quick note</label>
                  <textarea
                    value={detailNoteDraft}
                    onChange={(event) => setDetailNoteDraft(event.target.value)}
                    rows={4}
                    className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                    placeholder="Called vendor, payment delayed..."
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Flag</label>
                  <select
                    value={detailFlagDraft}
                    onChange={(event) => setDetailFlagDraft(event.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-md text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  >
                    {FLAG_OPTIONS.map((flagOption) => (
                      <option key={flagOption.value} value={flagOption.value}>{flagOption.label}</option>
                    ))}
                  </select>

                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={handleSaveDetails}
                      disabled={!hasUnsavedDetailChanges || isSavingDetails}
                      className="px-4 py-2 bg-zinc-900 text-white text-[11px] uppercase tracking-[0.12em] rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50"
                    >
                      {isSavingDetails ? 'Saving...' : 'Save to QuickBooks'}
                    </button>
                    {detailSaveMessage && <span className="text-xs text-emerald-700">{detailSaveMessage}</span>}
                  </div>
                </div>
              </div>

              {conflictItemsForSelectedPayment.length > 0 && (
                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Conflict history</p>
                  <div className="mt-2 space-y-2">
                    {conflictItemsForSelectedPayment.slice(0, 3).map((conflict) => (
                      <div key={conflict.id} className="rounded-md border border-zinc-100 px-3 py-2">
                        <p className="text-[11px] text-zinc-700">{conflict.changes.join(' · ')}</p>
                        <p className="text-[10px] text-zinc-400 mt-1">
                          {new Date(conflict.changedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCalendar;
